import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export type DocTocItem = { id: string; text: string; level: number };

const DocToc: React.FC = () => {
  const [items, setItems] = useState<DocTocItem[]>([]);

  const location = useLocation();
  const observerRef = useRef<MutationObserver | null>(null);

  const rebuildToc = () => {
    const headings = Array.from(
      document.querySelectorAll("article.prose h2, article.prose h3")
    ) as HTMLHeadingElement[];
    const mapped = headings.map((h) => ({
      id: h.id,
      text: h.textContent || "",
      level: h.tagName === "H2" ? 2 : 3,
    }));
    setItems(mapped);
  };

  useEffect(() => {
    rebuildToc();
    // Observe the article for async markdown render or content changes
    const article = document.querySelector("article.prose");
    if (article) {
      observerRef.current?.disconnect();
      observerRef.current = new MutationObserver(() => {
        rebuildToc();
      });
      observerRef.current.observe(article, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
    return () => {
      observerRef.current?.disconnect();
    };
  }, [location.pathname]);

  return (
    <aside className="hidden xl:block xl:w-64 xl:shrink-0 xl:sticky xl:top-16 xl:self-start">
      <div className="px-4 py-4">
        <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
          On this page
        </div>
        {items.length > 0 ? (
          <ul className="text-sm space-y-1">
            {items.map((it) => (
              <li key={it.id} className={it.level === 3 ? "ml-4" : ""}>
                <a
                  className="text-gray-700 dark:text-gray-200 hover:underline"
                  href={`#${it.id}`}
                >
                  {it.text}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-gray-400 dark:text-gray-500">
            No sections
          </div>
        )}
      </div>
    </aside>
  );
};

export default DocToc;
